import React, { forwardRef, useEffect, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import {
  PaymentRequestForm,
  type PaymentRequestFormProps,
} from '@features/payments/components/PaymentRequestForm';
import type { PaymentRequestFormData } from '@features/payments/utils/validation';

import { useActionEditor } from '../../store/hooks';
import type { PaymentRequestPayload } from '../../types';

interface PaymentRequestFormWrapperProps
  extends Omit<PaymentRequestFormProps, 'defaultValues' | 'onSubmit'> {
  // Action payload with nested structure
  actionPayload?: PaymentRequestPayload;

  // Submit handler that expects nested structure
  onSubmit: (payload: PaymentRequestPayload) => void | Promise<void>;

  // Enable draft functionality
  enableDraft?: boolean;

  // Whether to show the due date field (hidden when patient has payment method on file)
  showDueDate?: boolean;
}

// Inner component that uses the draft hook
const PaymentRequestFormWithDraft = forwardRef<
  HTMLFormElement,
  PaymentRequestFormWrapperProps & { actionId: string }
>(({ actionPayload, onSubmit, patientId, enableDraft = false, actionId, showDueDate, ...props }, ref) => {
  // Always call the hook (required by React rules)
  const draftEditor = useActionEditor<PaymentRequestPayload>(actionId);
  const isDraftEnabled = enableDraft;
  const formRef = useRef<UseFormReturn<PaymentRequestFormData> | null>(null);
  const previousValuesRef = useRef<Partial<PaymentRequestFormData>>();

  // Initialize draft on mount if enabled
  useEffect(() => {
    if (isDraftEnabled && actionPayload) {
      draftEditor.initializeDraft(actionPayload);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionPayload]);

  // Subscribe to form value changes if draft is enabled
  useEffect(() => {
    if (!isDraftEnabled || !formRef.current) return;

    const form = formRef.current;
    const subscription = form.watch(values => {
      // Skip if values haven't changed
      if (JSON.stringify(values) === JSON.stringify(previousValuesRef.current)) {
        return;
      }
      previousValuesRef.current = values;

      // Map form fields to payload fields
      const payloadUpdates: Partial<PaymentRequestPayload> = {};
      let hasChanges = false;

      if (values.amount !== undefined) {
        payloadUpdates.amount = values.amount;
        hasChanges = true;
      }
      if (values.reason !== undefined) {
        payloadUpdates.reason_for_payment = values.reason;
        hasChanges = true;
      }
      if (values.dueDate !== undefined) {
        payloadUpdates.due_date = values.dueDate || null;
        hasChanges = true;
      }
      if (values.appointmentId !== undefined) {
        payloadUpdates.appointment_id = values.appointmentId || null;
        hasChanges = true;
      }
      if (values.encounterId !== undefined) {
        payloadUpdates.encounter_id = values.encounterId;
        hasChanges = true;
      }

      if (hasChanges) {
        draftEditor.handleContentChange(payloadUpdates);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [isDraftEnabled, draftEditor]);

  // Get initial values from draft or action payload
  const initialPayload = isDraftEnabled ? draftEditor.draft?.data || actionPayload : actionPayload;

  // Convert action payload to form data structure
  const defaultValues: Partial<PaymentRequestFormData> | undefined = initialPayload
    ? {
        amount: initialPayload.amount || '',
        patientId: patientId || '',
        reason: initialPayload.reason_for_payment || '',
        dueDate: initialPayload.due_date || '',
        appointmentId: initialPayload.appointment_id || '',
        encounterId: initialPayload.encounter_id || '',
        actionId: actionId || '',
      }
    : undefined;

  // Convert form data to action payload structure
  const handleSubmit = async (formData: PaymentRequestFormData) => {
    const payload: PaymentRequestPayload = {
      patient_name: initialPayload?.patient_name || '', // Preserve existing patient_name
      appointment_date: initialPayload?.appointment_date || null, // Preserve existing appointment_date
      reason_for_payment: formData.reason || '',
      amount: formData.amount,
      due_date: formData.dueDate || null,
      encounter_id: formData.encounterId || '',
      appointment_id: formData.appointmentId || null,
    };

    // Save draft state before submitting if enabled
    if (isDraftEnabled) {
      await draftEditor.handleSave();
      draftEditor.clearDraft();
    }

    return onSubmit(payload);
  };
  return (
    <PaymentRequestForm
      {...props}
      actionId={actionId}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      showDueDate={showDueDate}
      formRef={formRef}
      ref={ref}
    />
  );
});

PaymentRequestFormWithDraft.displayName = 'PaymentRequestFormWithDraft';

// Main wrapper that conditionally uses draft functionality
export const PaymentRequestFormWrapper = forwardRef<
  HTMLFormElement,
  PaymentRequestFormWrapperProps
>(({ enableDraft = false, ...props }, ref) => {
  // If we have an actionId and drafts are enabled, use the draft version
  if (props.actionId && enableDraft) {
    return (
      <PaymentRequestFormWithDraft
        {...props}
        actionId={props.actionId}
        enableDraft={enableDraft}
        ref={ref}
      />
    );
  }

  // Otherwise, use the simple version without drafts
  const { actionPayload, onSubmit, patientId, showDueDate } = props;

  // Convert action payload to form data structure
  const defaultValues: Partial<PaymentRequestFormData> | undefined = actionPayload
    ? {
        amount: actionPayload.amount || '',
        patientId: patientId || '',
        reason: actionPayload.reason_for_payment || '',
        dueDate: actionPayload.due_date || '',
        appointmentId: actionPayload.appointment_id || '',
        encounterId: actionPayload.encounter_id || '',
        actionId: props.actionId || '',
      }
    : undefined;

  // Convert form data to action payload structure
  const handleSubmit = (formData: PaymentRequestFormData) => {
    const payload: PaymentRequestPayload = {
      patient_name: actionPayload?.patient_name || '',
      appointment_date: actionPayload?.appointment_date || null,
      reason_for_payment: formData.reason || '',
      amount: formData.amount,
      due_date: formData.dueDate || null,
      encounter_id: formData.encounterId || '',
      appointment_id: formData.appointmentId || null,
    };

    return onSubmit(payload);
  };

  return (
    <PaymentRequestForm
      {...props}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      showDueDate={showDueDate}
      ref={ref}
    />
  );
});

PaymentRequestFormWrapper.displayName = 'PaymentRequestFormWrapper';

export default PaymentRequestFormWrapper;
